import React from "react";
import { useTranslation } from "react-i18next";

import {
  UI_APPOINTMENT_CARD,
  UI_AVAILABILITY_CARD,
  UI_AVAILABILITY_SLOTS,
  UI_CALENDAR_PICKER,
  UI_CONFIRMATION_CARD,
  UI_DOCTOR_CARDS,
  UI_EVENT_CARD,
  UI_GPT_CHAT_INPUT,
  UI_GPT_RESPONSE_CARD,
  UI_LOCATION_CARD,
  UI_MEDICAL_RECORD_CARD,
  UI_MEDICATION_CARD,
  UI_SPECIALTY_PICKER,
  UI_TEXT_INPUT,
  UI_TIME_RANGE_PICKER,
  UI_TIME_SLOT_PICKER,
  UI_WEEKDAY_PICKER,
} from "../../constants/guidedUiTypes";
import GptChatInput from "../GptChat/GptChatInput.jsx";
import GptResponseCard from "../GptChat/GptResponseCard.jsx";
import "./GuidedFlowRenderer.css";

const normalizeFlowText = (value = "") =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const isSameFlowText = (left, right) =>
  Boolean(left && right && normalizeFlowText(left) === normalizeFlowText(right));

const UUID_LIKE_TEXT = /\b[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\b/i;

const useGuidedFlowLabels = () => {
  const { t } = useTranslation("assistantAi");
  const labels = t("guidedFlow", { returnObjects: true });
  return labels && typeof labels === "object" ? labels : {};
};

const readableDoctorName = (doctor = {}) =>
  String(
    doctor?.name ||
    doctor?.fullName ||
    doctor?.doctorName ||
    doctor?.medecinName ||
    doctor?.raw?.fullName ||
    doctor?.raw?.name ||
    ""
  ).trim();

const appointmentTitle = (appointment = {}) => {
  const title = String(appointment?.title || "").trim();
  if (title && !UUID_LIKE_TEXT.test(title) && !/^doctor\s*#/i.test(title) && !/^medecin\s*#/i.test(title)) {
    return title;
  }

  return (
    readableDoctorName(appointment?.doctor) ||
    appointment?.doctorName ||
    appointment?.medecinName ||
    "Médecin"
  );
};

export default function GuidedFlowRenderer({ ui, onContinue, onCancel, onShowMenu }) {
  const labels = useGuidedFlowLabels();

  if (ui?.type === UI_SPECIALTY_PICKER) {
    return <SpecialtyPicker ui={ui} onContinue={onContinue} />;
  }

  if (ui?.type === UI_DOCTOR_CARDS) {
    return <DoctorCards ui={ui} onContinue={onContinue} />;
  }

  if (ui?.type === UI_CALENDAR_PICKER) {
    return <CalendarPicker ui={ui} onContinue={onContinue} onCancel={onCancel} onShowMenu={onShowMenu} />;
  }

  if (ui?.type === UI_TIME_SLOT_PICKER) {
    return <TimeSlotPicker ui={ui} onContinue={onContinue} onCancel={onCancel} onShowMenu={onShowMenu} />;
  }

  if (ui?.type === UI_TIME_RANGE_PICKER) {
    return <TimeRangePicker ui={ui} onContinue={onContinue} onCancel={onCancel} onShowMenu={onShowMenu} />;
  }

  if (ui?.type === UI_TEXT_INPUT) {
    return <TextInputStep ui={ui} onContinue={onContinue} onCancel={onCancel} onShowMenu={onShowMenu} />;
  }

  if (ui?.type === UI_GPT_CHAT_INPUT) {
    return <GptChatInput {...(ui?.props || {})} onSubmit={onContinue} />;
  }

  if (ui?.type === UI_GPT_RESPONSE_CARD) {
    return <GptResponseCard {...(ui?.props || {})} onContinue={onContinue} onShowMenu={onShowMenu} />;
  }

  if (ui?.type === UI_WEEKDAY_PICKER) {
    return <WeekdayPicker ui={ui} onContinue={onContinue} onCancel={onCancel} onShowMenu={onShowMenu} />;
  }

  if (ui?.type === UI_AVAILABILITY_CARD || ui?.type === UI_AVAILABILITY_SLOTS) {
    return <AvailabilitySlots ui={ui} onContinue={onContinue} onShowMenu={onShowMenu} />;
  }

  if (ui?.type === UI_APPOINTMENT_CARD) {
    return <AppointmentCards ui={ui} onContinue={onContinue} onShowMenu={onShowMenu} />;
  }

  if (ui?.type === UI_EVENT_CARD) {
    return <EventCards ui={ui} onShowMenu={onShowMenu} />;
  }

  if (ui?.type === UI_MEDICAL_RECORD_CARD) {
    return <MedicalRecordCard ui={ui} onContinue={onContinue} onShowMenu={onShowMenu} />;
  }

  if (ui?.type === UI_MEDICATION_CARD) {
    return <MedicationCards ui={ui} onShowMenu={onShowMenu} />;
  }

  if (ui?.type === UI_LOCATION_CARD) {
    return <LocationCards ui={ui} onShowMenu={onShowMenu} />;
  }

  if (ui?.type === UI_CONFIRMATION_CARD) {
    return <ConfirmationCard ui={ui} onContinue={onContinue} onCancel={onCancel} onShowMenu={onShowMenu} />;
  }

  const props = ui?.props || {};
  const primaryAction = props.primaryAction;
  const secondaryAction = props.secondaryAction;

  return (
    <div className="guided-flow-renderer" data-guided-ui="guided-flow">
      <div className="guided-flow-renderer__status" data-status={props.status || "ready"} />

      <div className="guided-flow-renderer__content">
        {props.title && <div className="guided-flow-renderer__title">{props.title}</div>}
        {props.description && !isSameFlowText(props.description, props.title) && (
          <div className="guided-flow-renderer__description">{props.description}</div>
        )}
      </div>

      {(primaryAction || secondaryAction) && (
        <div className="guided-flow-renderer__actions">
          {secondaryAction && (
            <button
              type="button"
              className="guided-flow-renderer__button guided-flow-renderer__button--secondary"
              onClick={() => onCancel?.(props)}
            >
              {secondaryAction.label || labels.cancel}
            </button>
          )}

          {primaryAction && (
            <button
              type="button"
              className="guided-flow-renderer__button guided-flow-renderer__button--primary"
              onClick={() => onContinue?.(props, primaryAction.payload || {})}
            >
              {primaryAction.label || labels.continue}
            </button>
          )}
        </div>
      )}

    </div>
  );
}

function SectionShell({ ui, children, footer, hideDescription = false }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const showDescription = !hideDescription && props.description && !isSameFlowText(props.description, props.title);

  return (
    <div className="guided-flow-renderer guided-flow-renderer--section" data-guided-ui={ui?.type}>
      <div className="guided-flow-renderer__status" data-status={props.status || "ready"} />
      <div className="guided-flow-renderer__content">
        {props.title && <div className="guided-flow-renderer__title">{props.title}</div>}
        {showDescription && (
          <div className="guided-flow-renderer__description">{props.description}</div>
        )}
        {children}
        {footer}
      </div>
    </div>
  );
}

function SpecialtyPicker({ ui, onContinue }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const options = Array.isArray(props.options) ? props.options : [];
  const initialVisibleCount = Math.max(1, Number(props.initialVisibleCount) || 6);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const visibleOptions = isExpanded ? options : options.slice(0, initialVisibleCount);
  const hiddenCount = Math.max(0, options.length - initialVisibleCount);
  const optionSignature = options.map((option) => option.id).join("|");

  React.useEffect(() => {
    setIsExpanded(false);
  }, [optionSignature]);

  return (
    <SectionShell ui={ui}>
      <div className="guided-choice-grid">
        {visibleOptions.map((option) => (
          <button
            type="button"
            className="guided-choice-button"
            key={option.id}
            onClick={() => onContinue?.(props, { specialty: option.id })}
          >
            {option.label}
          </button>
        ))}
      </div>
      {hiddenCount > 0 && (
        <button
          type="button"
          className="guided-list-toggle"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((current) => !current)}
        >
          {isExpanded
            ? props.showLessLabel || labels.showLess
            : `${props.showMoreLabel || labels.showMore} (${hiddenCount})`}
        </button>
      )}
    </SectionShell>
  );
}

function DoctorCards({ ui, onContinue }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const doctors = props.doctors || [];

  return (
    <SectionShell ui={ui}>
      {!doctors.length && <div className="guided-empty">{props.emptyMessage || labels.noResult}</div>}
      <div className="guided-card-list">
        {doctors.map((doctor, index) => (
          <div className="guided-doctor-card" key={`${doctor.id || doctor.name}-${index}`}>
            <div className="guided-doctor-card__main">
              <strong>{doctor.name || doctor.fullName || labels.doctor}</strong>
              <span>{doctor.specialty || doctor.specialite || labels.specialtyMissing}</span>
              {doctor.address && <small>{doctor.address}</small>}
            </div>
            <button
              type="button"
              className="guided-flow-renderer__button guided-flow-renderer__button--primary"
              onClick={() => onContinue?.(props, { doctorId: doctor.id || doctor.doctorId || doctor.medecinId, doctor })}
              title={doctor.registered ? labels.chooseDoctor : labels.inviteDoctor}
            >
              {doctor.registered ? labels.choose : labels.invite}
            </button>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

function TextInputStep({ ui, onContinue, onCancel, onShowMenu }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const [value, setValue] = React.useState(props.defaultValue || "");

  const submit = () => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return;
    onContinue?.(props, { [props.fieldName || "value"]: trimmed });
  };

  return (
    <SectionShell
      ui={ui}
      footer={
        <div className="guided-flow-renderer__actions">
          <button type="button" className="guided-flow-renderer__button guided-flow-renderer__button--secondary" onClick={() => onCancel?.(props)}>
            {labels.cancel}
          </button>
          <button type="button" className="guided-flow-renderer__button guided-flow-renderer__button--primary" onClick={submit} disabled={!String(value || "").trim()}>
            {props.submitLabel || labels.continue}
          </button>
        </div>
      }
    >
      <input
        className="guided-text-input"
        type="text"
        value={value}
        placeholder={props.placeholder || ""}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") submit();
        }}
      />
    </SectionShell>
  );
}

function EventCards({ ui, onShowMenu }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const events = props.events || [];

  return (
    <SectionShell ui={ui}>
      {!events.length && <div className="guided-empty">{props.emptyMessage || labels.noEvent}</div>}
      <div className="guided-card-list">
        {events.map((event) => (
          <div className="guided-event-card" key={event.id || `${event.title}-${event.timeLabel}`}>
            <div className="guided-event-card__main">
              <strong>{event.title || labels.event}</strong>
              <span>{event.subtitle || ""}</span>
              {event.timeLabel && <small>{event.timeLabel}</small>}
              {event.reason && <em>{event.reason}</em>}
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

function MedicalRecordCard({ ui, onContinue, onShowMenu }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const items = props.items || [];
  const choices = props.choices || [];
  const links = props.links || [];
  const sections = props.sections || [];

  return (
    <SectionShell
      ui={ui}
      footer={
        <CardFooterActions
          routeUrl={props.routeUrl || links[0]?.url}
          routeLabel={links[0]?.text || labels.openRecord}
        />
      }
    >
      {props.patient?.name && <div className="guided-record-patient">{props.patient.name}</div>}
      {!items.length && !choices.length && <div className="guided-empty">{props.emptyMessage || labels.noInfo}</div>}
      {!!items.length && (
        <div className="guided-record-grid">
          {items.map((item) => (
            <div className="guided-record-metric" key={`${item.label}-${item.value}`}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
        </div>
      )}
      {!!choices.length && (
        <div className="guided-card-list">
          {choices.map((choice) => (
            <button
              type="button"
              className="guided-record-choice guided-record-choice--button"
              key={choice.id || choice.name}
              onClick={() => onContinue?.(props, { patientName: choice.name, patientId: choice.id })}
            >
              <span className="guided-record-choice__text">
                <strong>{choice.name}</strong>
                {choice.subtitle && <span>{choice.subtitle}</span>}
              </span>
              <em>{props.choiceActionLabel || "Voir"}</em>
            </button>
          ))}
        </div>
      )}
      {!!sections.length && (
        <div className="guided-record-sections">
          {sections.map((section) => (
            <div className="guided-record-section" key={section.id || section.title}>
              <div className="guided-record-section__title">{section.title}</div>
              {!section.items?.length && <div className="guided-empty guided-empty--compact">{section.emptyMessage || labels.noInfo}</div>}
              {!!section.items?.length && section.metric && (
                <div className="guided-record-grid">
                  {section.items.map((item) => (
                    <div className="guided-record-metric" key={`${section.id}-${item.label}`}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
              )}
              {!!section.items?.length && !section.metric && (
                <div className="guided-card-list">
                  {section.items.map((item) => (
                    <div className="guided-medication-card" key={`${section.id}-${item.id || item.name}`}>
                      <div className="guided-medication-card__main">
                        <strong>{item.name || item.label}</strong>
                        {item.dose && <span>{item.dose}</span>}
                        {item.schedule && <small>{item.schedule}</small>}
                        {item.status && <em>{item.status}</em>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionShell>
  );
}

function MedicationCards({ ui, onShowMenu }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const medications = props.medications || [];

  return (
    <SectionShell
      ui={ui}
      footer={
        <CardFooterActions
          routeUrl={props.routeUrl}
          routeLabel="Ouvrir mes medicaments"
        />
      }
    >
      {!medications.length && <div className="guided-empty">{props.emptyMessage || labels.noInfo}</div>}
      <div className="guided-card-list">
        {medications.map((medication) => (
          <div className="guided-medication-card" key={medication.id || medication.name}>
            <div className="guided-medication-card__main">
              <strong>{medication.name || "Medicament"}</strong>
              {medication.dose && <span>{medication.dose}</span>}
              {medication.schedule && <small>{medication.schedule}</small>}
              {medication.status && <em>{medication.status}</em>}
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

function LocationCards({ ui, onShowMenu }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const locations = props.locations || [];

  const openAction = (action) => {
    if (!action?.url) return;
    if (action.external) {
      window.open(action.url, "_blank", "noopener,noreferrer");
      return;
    }
    window.location.assign(action.url);
  };

  return (
    <SectionShell ui={ui}>
      {!locations.length && <div className="guided-empty">{props.emptyMessage || labels.noInfo}</div>}
      <div className="guided-card-list">
        {locations.map((location) => (
          <div className="guided-location-card" key={location.id || location.name}>
            <div className="guided-location-card__main">
              <div className="guided-location-card__topline">
                <strong>{location.name || "Lieu medical"}</strong>
                {location.distance != null && <em className="guided-location-card__distance">{location.distance} km</em>}
              </div>
              {location.category && <span className="guided-location-card__category">{location.category}</span>}
              {location.address && <small>{location.address}</small>}
              <div className="guided-location-card__meta">
                {location.rating != null && <em>{location.rating}/5</em>}
                {location.phone && <em>{location.phone}</em>}
              </div>
            </div>
            {!!location.actions?.length && (
              <div className="guided-location-card__actions">
                {location.actions.slice(0, 2).map((action) => (
                  <button
                    type="button"
                    className="guided-flow-renderer__button guided-flow-renderer__button--secondary"
                    key={`${location.id}-${action.type}`}
                    onClick={() => openAction(action)}
                  >
                    {action.label || "Ouvrir"}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

function CardFooterActions({ routeUrl, routeLabel }) {
  if (!routeUrl) return null;

  return (
    <div className="guided-flow-renderer__actions">
      <button
        type="button"
        className="guided-flow-renderer__button guided-flow-renderer__button--secondary"
        onClick={() => window.location.assign(routeUrl)}
      >
        {routeLabel || "Ouvrir"}
      </button>
    </div>
  );
}

function AppointmentCards({ ui, onContinue, onShowMenu }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const appointments = props.appointments || [];

  return (
    <SectionShell ui={ui}>
      {!appointments.length && <div className="guided-empty">{props.emptyMessage || labels.noInfo}</div>}
      <div className="guided-card-list">
        {appointments.map((appointment) => (
          <div className="guided-appointment-card" key={appointment.id}>
            <div className="guided-appointment-card__main">
              <strong>{appointmentTitle(appointment)}</strong>
              <span>{appointment.subtitle || ""}</span>
              {appointment.timeLabel && <small>{appointment.timeLabel}</small>}
              {appointment.status && <em>{appointment.status}</em>}
            </div>
            {props.selectable && (
              <button
                type="button"
                className="guided-flow-renderer__button guided-flow-renderer__button--primary"
                onClick={() => onContinue?.(props, { appointmentId: appointment.id })}
              >
                {props.actionLabel || labels.choose}
              </button>
            )}
            {Array.isArray(appointment.actions) && appointment.actions.length > 0 && (
              <div className="guided-appointment-card__actions">
                {appointment.actions.map((action) => (
                  <button
                    type="button"
                    className={`guided-flow-renderer__button ${
                      action.variant === "secondary"
                        ? "guided-flow-renderer__button--secondary"
                        : "guided-flow-renderer__button--primary"
                    }`}
                    key={action.id || action.label}
                    onClick={() => onContinue?.(props, action.payload || {})}
                  >
                    {action.label || labels.continue}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

const CalendarPicker = React.memo(function CalendarPicker({ ui, onContinue, onCancel, onShowMenu }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const [periodMode, setPeriodMode] = React.useState(false);
  const [periodStart, setPeriodStart] = React.useState(null);
  const optionMap = React.useMemo(() => {
    const map = new Map();
    (props.options || []).forEach((option) => map.set(option.value, option));
    return map;
  }, [props.options]);
  const initialMonth = React.useMemo(() => {
    const firstValue = props.options?.[0]?.value || new Date().toISOString().slice(0, 10);
    const parsed = new Date(`${firstValue}T00:00:00`);
    return new Date(parsed.getFullYear(), parsed.getMonth(), 1);
  }, [props.options]);
  const [visibleMonth, setVisibleMonth] = React.useState(initialMonth);
  const minDateValue = props.minDate || new Date().toISOString().slice(0, 10);
  const minDate = new Date(`${minDateValue}T00:00:00`);
  const maxYear = Number(props.maxYear || minDate.getFullYear() + 5);
  const maxDate = new Date(maxYear, 11, 31);

  if (props.loading || props.status === "loading") {
    return (
      <SectionShell ui={ui}>
        <div className="guided-calendar-skeleton" aria-label="Chargement du calendrier">
          <div className="guided-calendar-skeleton__header" />
          <div className="guided-calendar-skeleton__weekdays">
            {Array.from({ length: 7 }, (_, index) => (
              <span key={`weekday-${index}`} />
            ))}
          </div>
          <div className="guided-calendar-skeleton__grid">
            {Array.from({ length: 35 }, (_, index) => (
              <span className="guided-calendar-skeleton__day" key={`day-${index}`} />
            ))}
          </div>
        </div>
      </SectionShell>
    );
  }

  const handleDateClick = (option) => {
    if (!props.allowPeriod || !periodMode) {
      onContinue?.(props, { date: option.value });
      return;
    }
    if (!periodStart) {
      setPeriodStart(option.value);
      return;
    }
    const start = periodStart <= option.value ? periodStart : option.value;
    const end = periodStart <= option.value ? option.value : periodStart;
    onContinue?.(props, { periodStart: start, periodEnd: end });
  };

  const monthLabel = visibleMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = (firstDay.getDay() + 6) % 7;
  const monthCells = [
    ...Array.from({ length: startOffset }, (_, index) => ({ id: `blank-${index}`, blank: true })),
    ...Array.from({ length: daysInMonth }, (_, index) => {
      const day = index + 1;
      const value = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const fallbackOption = {
        id: value,
        value,
        label: `${String(day).padStart(2, "0")}/${String(month + 1).padStart(2, "0")}`,
      };
      return { id: value, value, day, option: optionMap.get(value) || fallbackOption };
    }),
  ];
  const canGoPrev = new Date(year, month, 1) > new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const canGoNext = new Date(year, month + 1, 1) <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
  const isInSelectedPeriod = (value) => {
    if (!periodMode || !periodStart || !value) return false;
    return value === periodStart;
  };

  return (
    <SectionShell
      ui={ui}
      footer={
        <div className="guided-flow-renderer__actions">
          <button type="button" className="guided-flow-renderer__button guided-flow-renderer__button--secondary" onClick={() => onCancel?.(props)}>
            {labels.cancel}
          </button>
        </div>
      }
    >
      {props.allowPeriod && (
        <div className="guided-mode-row">
          <button
            type="button"
            className={`guided-choice-button ${!periodMode ? "guided-choice-button--selected" : ""}`}
            onClick={() => {
              setPeriodMode(false);
              setPeriodStart(null);
            }}
          >
            {props.singleLabel || labels.singleDay}
          </button>
          <button
            type="button"
            className={`guided-choice-button ${periodMode ? "guided-choice-button--selected" : ""}`}
            onClick={() => setPeriodMode(true)}
          >
            {props.periodLabel || labels.periodMode}
          </button>
        </div>
      )}
      {periodMode && (
        <div className="guided-empty">
          {periodStart
            ? labels.periodStartSelected?.replace("{{date}}", periodStart)
            : labels.choosePeriodStart}
        </div>
      )}
      {!(props.options || []).length && <div className="guided-empty">{props.emptyMessage || labels.noSlot}</div>}
      <div className="guided-calendar">
        <div className="guided-calendar__header">
          <button
            type="button"
            className="guided-calendar__nav"
            onClick={() => setVisibleMonth(new Date(year, month - 1, 1))}
            disabled={!canGoPrev}
            aria-label={labels.previousMonth}
          >
            &lt;
          </button>
          <strong>{monthLabel}</strong>
          <button
            type="button"
            className="guided-calendar__nav"
            onClick={() => setVisibleMonth(new Date(year, month + 1, 1))}
            disabled={!canGoNext}
            aria-label={labels.nextMonth}
          >
            &gt;
          </button>
        </div>
        <div className="guided-calendar__weekdays">
          {["L", "M", "M", "J", "V", "S", "D"].map((item, index) => (
            <span key={`${item}-${index}`}>{item}</span>
          ))}
        </div>
        <div className="guided-calendar__grid">
          {monthCells.map((cell) => {
            if (cell.blank) return <span className="guided-calendar__blank" key={cell.id} />;
            const option = cell.option;
            const cellDate = new Date(`${cell.value}T00:00:00`);
            const enabled = Boolean(option) && (!props.allowAnyFutureDate ? optionMap.has(cell.value) : cellDate >= minDate && cellDate <= maxDate);
            return (
              <button
                type="button"
                className={`guided-calendar__day ${option?.marked ? "guided-calendar__day--marked" : ""} ${isInSelectedPeriod(cell.value) ? "guided-calendar__day--selected" : ""}`}
                key={cell.id}
                onClick={() => enabled && handleDateClick(option)}
                disabled={!enabled}
                title={option?.title || ""}
              >
                <span>{cell.day}</span>
                {option?.badge && <small>{option.badge}</small>}
              </button>
            );
          })}
        </div>
      </div>
    </SectionShell>
  );
});

function WeekdayPicker({ ui, onContinue, onCancel, onShowMenu }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const defaults = props.defaultSelected || [];
  const [selected, setSelected] = React.useState(defaults);

  const toggle = (day) => {
    setSelected((current) =>
      current.map(Number).includes(Number(day))
        ? current.filter((item) => Number(item) !== Number(day))
        : [...current, day]
    );
  };

  return (
    <SectionShell
      ui={ui}
      footer={
        <div className="guided-flow-renderer__actions">
          <button type="button" className="guided-flow-renderer__button guided-flow-renderer__button--secondary" onClick={() => onCancel?.(props)}>
            {labels.cancel}
          </button>
          <button
            type="button"
            className="guided-flow-renderer__button guided-flow-renderer__button--primary"
            onClick={() => onContinue?.(props, { weekdays: selected })}
            disabled={!selected.length}
          >
            {labels.continue}
          </button>
        </div>
      }
    >
      <div className="guided-choice-grid">
        {(props.options || []).map((option) => {
          const active = selected.map(Number).includes(Number(option.dayOfWeek));
          return (
            <button
              type="button"
              className={`guided-choice-button ${active ? "guided-choice-button--selected" : ""}`}
              key={option.id}
              onClick={() => toggle(option.dayOfWeek)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </SectionShell>
  );
}

function TimeSlotPicker({ ui, onContinue, onCancel, onShowMenu }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const slots = props.slots || [];
  const [selectedSlots, setSelectedSlots] = React.useState([]);
  const isBlockingFlow =
    props.multiSelect === true ||
    props.flowId === "doctor_block_slot" ||
    /bloquer|block/i.test(`${props.title || ""} ${props.primaryAction?.label || ""}`);

  const getSlotId = (slot, index) => String(slot.id || slot.slotId || slot.value || slot.label || index);

  React.useEffect(() => {
    setSelectedSlots([]);
  }, [props.flowId, props.stepId, props.date, slots.length]);

  const toggleSlot = (slotId) => {
    setSelectedSlots((current) =>
      current.includes(slotId)
        ? current.filter((id) => id !== slotId)
        : [...current, slotId]
    );
  };

  const handleSlotClick = (slot, index) => {
    const slotId = getSlotId(slot, index);
    if (isBlockingFlow) {
      toggleSlot(slotId);
      return;
    }
    onContinue?.(props, { slotId });
  };

  const handleConfirm = () => {
    if (!selectedSlots.length) return;
    onContinue?.(props, {
      slotId: selectedSlots[0],
      slotIds: selectedSlots,
    });
  };
  const formattedDate = props.date
    ? new Date(`${props.date}T00:00:00`).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "";

  return (
    <SectionShell
      ui={ui}
      footer={
        <div className="guided-flow-renderer__actions">
          {false && isBlockingFlow && (
            <button
              type="button"
              className="guided-flow-renderer__button guided-flow-renderer__button--primary guided-slot-confirm"
              onClick={handleConfirm}
              disabled={!selectedSlots.length}
            >
              {labels.blockSelected
                ?.replace("{{count}}", String(selectedSlots.length))
                .replace("{{plural}}", selectedSlots.length > 1 ? "x" : "")}
            </button>
          )}
          <button type="button" className="guided-flow-renderer__button guided-flow-renderer__button--secondary" onClick={() => onCancel?.(props)}>
            {labels.cancel}
          </button>
        </div>
      }
    >
      {slots.length > 0 && (
        <div className="guided-slot-summary">
          <strong>{formattedDate ? `${labels.slotsAvailableTitle} - ${formattedDate}` : labels.slotsAvailableTitle}</strong>
          <span>
            {slots.length} créneau{slots.length > 1 ? "x" : ""} disponible{slots.length > 1 ? "s" : ""}
          </span>
        </div>
      )}
      {!slots.length && <div className="guided-empty">{props.emptyMessage || labels.noSlot}</div>}
      {slots.length > 0 && isBlockingFlow && (
        <div className="guided-slot-helper">{labels.selectSlotsToBlock}</div>
      )}
      {slots.length > 0 && isBlockingFlow && (
        <div className="guided-slot-action-bar">
          {props.blockAllLabel && (
            <button
              type="button"
              className="guided-flow-renderer__button guided-flow-renderer__button--secondary"
              onClick={() => onContinue?.(props, { action: "block_day" })}
            >
              {props.blockAllLabel}
            </button>
          )}
          <button
            type="button"
            className="guided-flow-renderer__button guided-flow-renderer__button--primary guided-slot-confirm"
            onClick={handleConfirm}
            disabled={!selectedSlots.length}
          >
            {selectedSlots.length
              ? `${props.confirmLabel || labels.block} ${selectedSlots.length} creneau${selectedSlots.length > 1 ? "x" : ""}`
              : props.emptySelectionLabel || labels.selectSlot}
          </button>
        </div>
      )}
      <div className="guided-choice-grid guided-choice-grid--slots">
        {slots.map((slot, index) => {
          const slotId = getSlotId(slot, index);
          const selected = selectedSlots.includes(slotId);
          return (
            <button
              type="button"
              className={`guided-choice-button guided-slot-toggle ${selected ? "guided-slot-toggle--selected" : ""}`}
              key={slotId}
              onClick={() => handleSlotClick(slot, index)}
              aria-pressed={isBlockingFlow ? selected : undefined}
            >
              {slot.label || slot.startTime}
            </button>
          );
        })}
      </div>
    </SectionShell>
  );
}

function TimeRangePicker({ ui, onContinue, onCancel, onShowMenu }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const durations = props.durations || [];
  const defaultDuration = props.defaultDuration || durations[0]?.value || 30;
  const includeDates = Boolean(props.includeDates);
  const showDurations = props.showDurations !== false;
  const today = new Date().toISOString().slice(0, 10);
  const [duration, setDuration] = React.useState(defaultDuration);
  const [startDate, setStartDate] = React.useState(props.defaultStartDate || props.date || today);
  const [endDate, setEndDate] = React.useState(props.defaultEndDate || props.date || props.defaultStartDate || today);
  const [start, setStart] = React.useState(props.defaultStart || "09:00");
  const [end, setEnd] = React.useState(props.defaultEnd || "12:00");
  const canSubmit = React.useMemo(() => {
    if (!start || !end) return false;
    if (!includeDates) return start < end;
    if (!startDate || !endDate) return false;
    return new Date(`${startDate}T${start}`) < new Date(`${endDate}T${end}`);
  }, [end, endDate, includeDates, start, startDate]);

  const submit = () => {
    if (!canSubmit) return;
    onContinue?.(props, {
      date: includeDates ? startDate : props.date,
      startDate: includeDates ? startDate : props.date,
      endDate: includeDates ? endDate : props.date,
      periodStart: props.periodStart,
      periodEnd: props.periodEnd,
      weekdays: props.weekdays,
      start,
      end,
      duration,
    });
  };

  return (
    <SectionShell
      ui={ui}
      footer={
        <div className="guided-flow-renderer__actions">
          <button type="button" className="guided-flow-renderer__button guided-flow-renderer__button--secondary" onClick={() => onCancel?.(props)}>
            {labels.cancel}
          </button>
          <button type="button" className="guided-flow-renderer__button guided-flow-renderer__button--primary" onClick={submit} disabled={!canSubmit}>
            {labels.continue}
          </button>
        </div>
      }
    >
      {showDurations && (
        <div className="guided-duration-row">
          {durations.map((item) => (
            <button
              type="button"
              className={`guided-choice-button ${Number(duration) === Number(item.value) ? "guided-choice-button--selected" : ""}`}
              key={item.id || item.value}
              onClick={() => setDuration(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
      {includeDates && (
        <div className="guided-time-manual-grid guided-time-manual-grid--dates">
          <label className="guided-time-field">
            <span>{props.startDateLabel || labels.startDate}</span>
            <input type="date" min={props.minDate || today} value={startDate} onChange={(event) => {
              const value = event.target.value;
              setStartDate(value);
              if (endDate < value) setEndDate(value);
            }} />
          </label>
          <label className="guided-time-field">
            <span>{props.endDateLabel || labels.endDate}</span>
            <input type="date" min={startDate || props.minDate || today} value={endDate} onChange={(event) => setEndDate(event.target.value)} />
          </label>
        </div>
      )}
      <div className="guided-time-manual-grid">
        <label className="guided-time-field">
          <span>{props.startLabel || labels.start}</span>
          <input type="time" value={start} onChange={(event) => setStart(event.target.value)} />
        </label>
        <label className="guided-time-field">
          <span>{props.endLabel || labels.end}</span>
          <input type="time" value={end} onChange={(event) => setEnd(event.target.value)} />
        </label>
      </div>
      {!canSubmit && <div className="guided-result guided-result--warning">Choisissez une fin apres le debut.</div>}
    </SectionShell>
  );
}

function AvailabilitySlots({ ui, onContinue, onShowMenu }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const slots = props.slots || [];
  const primaryAction = props.primaryAction;
  const isSlotAvailable = (slot) => {
    const status = String(slot.status || slot.statusLabel || "").toLowerCase();
    if (slot.isAvailable === false || slot.available === false) return false;
    if (status.includes("bloqu") || status.includes("indisponible") || status.includes("unavailable")) return false;
    return true;
  };
  const reasonLabel = (slot) => {
    const raw = String(slot.unavailableReason || slot.reasonLabel || slot.reason || "").toLowerCase();
    if (raw.includes("appointment") || raw.includes("rdv") || raw.includes("rendez")) return labels.appointmentReason;
    if (raw.includes("event") || raw.includes("even") || raw.includes("évén")) return labels.event;
    if (raw.includes("bloqu") || raw.includes("block")) return labels.blockedReason;
    if (raw.includes("occup")) return labels.busyReason;
    return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : labels.unavailable;
  };
  const reasonDetail = (slot) => {
    return slot.unavailableLabel || slot.reasonDetail || slot.busyLabel || slot.patientName || slot.eventTitle || "";
  };
  const availableCount = slots.filter(isSlotAvailable).length;
  const blockedCount = slots.length - availableCount;

  return (
    <SectionShell
      ui={ui}
      hideDescription
      footer={
        <div className="guided-flow-renderer__actions">
          {primaryAction && (
            <button
              type="button"
              className="guided-flow-renderer__button guided-flow-renderer__button--secondary"
              onClick={() => onContinue?.(props, primaryAction.payload || {})}
            >
              {primaryAction.label || labels.showMore}
            </button>
          )}
        </div>
      }
    >
      {slots.length > 0 && (
        <div className="guided-slot-summary">
          <strong>
            {props.date
              ? `Disponibilités — ${new Date(`${props.date}T00:00:00`).toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}`
              : "Disponibilités"}
          </strong>
          <span>
            {availableCount} creneau{availableCount > 1 ? "x" : ""} disponible{availableCount > 1 ? "s" : ""}
            {blockedCount > 0 ? ` - ${blockedCount} bloque${blockedCount > 1 ? "s" : ""}` : ""}
          </span>
        </div>
      )}
      {!slots.length && <div className="guided-empty">{props.emptyMessage || labels.noSlot}</div>}
      <div className="guided-card-list">
        {slots.map((slot) => (
          (() => {
            const available = isSlotAvailable(slot);
            const detail = reasonDetail(slot);
            return (
              <div
                className={`guided-slot-card ${available ? "guided-slot-card--available" : "guided-slot-card--blocked"}`}
                key={`${slot.id || slot.label}`}
              >
                <strong>{slot.label || slot.displayLabel || slot.startTime}</strong>
                <span>{available ? (slot.statusLabel || "Disponible") : `Indisponible - ${reasonLabel(slot)}`}</span>
                {!available && detail && <small>{detail}</small>}
              </div>
            );
          })()
        ))}
      </div>
    </SectionShell>
  );
}

function ConfirmationCard({ ui, onContinue, onCancel, onShowMenu }) {
  const props = ui?.props || {};
  const labels = useGuidedFlowLabels();
  const primaryAction = props.primaryAction;
  const isExecutionConfirmation = primaryAction?.payload?.confirm === true;
  const resultText = props.description && !isSameFlowText(props.description, props.title)
    ? props.description
    : "";

  return (
    <SectionShell
      ui={ui}
      hideDescription
        footer={
          <div className="guided-flow-renderer__actions">
            {isExecutionConfirmation && (
              <button type="button" className="guided-flow-renderer__button guided-flow-renderer__button--secondary" onClick={() => onCancel?.(props)}>
                {labels.cancel}
              </button>
            )}
            {!isExecutionConfirmation && props.ok === false && (
              <button type="button" className="guided-flow-renderer__button guided-flow-renderer__button--secondary" onClick={() => onCancel?.(props)}>
                Fermer
              </button>
            )}
            {!isExecutionConfirmation && primaryAction && (
              <button
                type="button"
                className="guided-flow-renderer__button guided-flow-renderer__button--secondary"
              onClick={() => onContinue?.(props, primaryAction.payload || {})}
            >
                {primaryAction.label || labels.continue}
              </button>
            )}
            {isExecutionConfirmation && primaryAction && (
              <button
                type="button"
                className="guided-flow-renderer__button guided-flow-renderer__button--primary"
                onClick={() => onContinue?.(props, primaryAction.payload || {})}
              >
                {primaryAction.label || "Confirmer"}
              </button>
            )}
          </div>
        }
      >
      {resultText && (
        <div className={props.ok === false ? "guided-result guided-result--warning" : "guided-result"}>
          {resultText}
        </div>
      )}
    </SectionShell>
  );
}
